import { Injectable, inject } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import {
  BusinessProfileApiService,
  BusinessProfileFeatureFlagResponse,
} from '../business-profile/business-profile-api.service';
import { BooleanFeatureFlagApiResponse } from './models/boolean-feature-flag-api-response.model';
import { FinancialAnalysisFeatureState } from './models/financial-analysis-feature-state.model';

const FEATURE_FLAG_ID =
  'cycle-risk-portfolio-financial-analysis-enable' as const;
const FEATURE_FLAG_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class BusinessProfileFeatureFlagService {
  private readonly businessProfileApiService = inject(
    BusinessProfileApiService,
  );

  private readonly featureStateSubject =
    new ReplaySubject<FinancialAnalysisFeatureState>(1);
  private initialized = false;

  get featureState$(): Observable<FinancialAnalysisFeatureState> {
    return this.featureStateSubject.asObservable();
  }

  /**
   * Initializes the feature flag state by fetching from the API.
   * Idempotent - can be called multiple times but will only execute once.
   */
  public initialize(): void {
    this.initializeFeatureState();
  }

  private initializeFeatureState(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) {
        return;
      }

      settled = true;
      this.featureStateSubject.next(this.createUnavailableState());
    }, FEATURE_FLAG_TIMEOUT_MS);

    this.businessProfileApiService
      .getFeatureFlag(FEATURE_FLAG_ID)
      .then((response: BusinessProfileFeatureFlagResponse) => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timeoutId);
        this.featureStateSubject.next(this.normalizeResponse(response));
      })
      .catch(() => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timeoutId);
        this.featureStateSubject.next(this.createUnavailableState());
      });
  }

  private normalizeResponse(response: unknown): FinancialAnalysisFeatureState {
    if (!this.isBooleanFeatureFlagResponse(response)) {
      return this.createInvalidState();
    }

    if (response.featureFlagResolution !== 'evaluated') {
      return this.createUnavailableState();
    }

    if (response.kind !== 'boolean' || response.key !== FEATURE_FLAG_ID) {
      return this.createInvalidState();
    }

    return {
      featureFlagId: FEATURE_FLAG_ID,
      isEnabled: response.value,
      source: 'resolved',
      resolvedAt: Date.now(),
    };
  }

  private createUnavailableState(): FinancialAnalysisFeatureState {
    return {
      featureFlagId: FEATURE_FLAG_ID,
      isEnabled: false,
      source: 'unavailable',
      resolvedAt: Date.now(),
    };
  }

  private createInvalidState(): FinancialAnalysisFeatureState {
    return {
      featureFlagId: FEATURE_FLAG_ID,
      isEnabled: false,
      source: 'invalid',
      resolvedAt: Date.now(),
    };
  }

  private isBooleanFeatureFlagResponse(
    response: unknown,
  ): response is BooleanFeatureFlagApiResponse {
    if (typeof response !== 'object' || response === null) {
      return false;
    }

    const candidate = response as Partial<BooleanFeatureFlagApiResponse>;

    return (
      typeof candidate.name === 'string' &&
      typeof candidate.kind === 'string' &&
      typeof candidate.key === 'string' &&
      typeof candidate._version === 'number' &&
      typeof candidate.creationDate === 'number' &&
      typeof candidate.value === 'boolean' &&
      (candidate.featureFlagResolution === 'evaluated' ||
        candidate.featureFlagResolution === 'missing' ||
        candidate.featureFlagResolution === 'timeout')
    );
  }
}
